import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, Phone, CalendarCheck2 } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { NAV_LINKS } from "@/lib/constants";
import { useSettings } from "@/lib/SiteContent";
import { cn } from "@/lib/utils";

const Logo = ({ className = "", variant = "full" }) => (
  <Link
    to="/"
    aria-label="Seetai Properties Home"
    data-testid="navbar-logo"
    className={cn("flex items-center group", className)}
  >
    {variant === "emblem" ? (
      <img
        src="/brand/seetai-emblem-512.png"
        alt="Seetai Properties"
        loading="eager"
        decoding="async"
        draggable={false}
        className="block h-11 w-11 select-none transition-transform duration-300 group-hover:scale-105"
      />
    ) : (
      <img
        src="/brand/seetai-logo-full.png"
        alt="Seetai Properties — Crafting Luxury. Building Legacy."
        loading="eager"
        decoding="async"
        draggable={false}
        className="block h-12 w-auto select-none transition-opacity duration-300 group-hover:opacity-95"
      />
    )}
  </Link>
);

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const settings = useSettings();
  const contact = settings?.contact || {};

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 16);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <header
      data-testid="site-navbar"
      className={cn(
        "fixed top-0 left-0 right-0 z-50 transition-all duration-500",
        scrolled
          ? "bg-black/65 backdrop-blur-2xl border-b border-white/10 shadow-[0_8px_30px_rgba(0,0,0,0.45)]"
          : "bg-gradient-to-b from-black/55 to-transparent backdrop-blur-md"
      )}
    >
      <div className="container-narrow flex h-20 items-center justify-between gap-2 sm:gap-3 lg:gap-2 xl:gap-4">
        {/* Use emblem-only on small screens to save space; full lockup on md+ */}
        <Logo className="md:hidden" variant="emblem" />
        <Logo className="hidden md:flex shrink-0" variant="full" />

        {/* Desktop nav — centred between logo and CTA cluster.
            whitespace-nowrap on each link prevents 'About Us' / 'Contact Us'
            from wrapping into two lines at lg widths. */}
        <nav
          className="hidden lg:flex flex-1 items-center justify-center gap-1 xl:gap-2"
          aria-label="Primary"
        >
          {NAV_LINKS.map((link) => (
            <NavLink
              key={link.to}
              to={link.to}
              data-testid={`nav-link-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                cn(
                  "relative whitespace-nowrap px-3 xl:px-4 py-2 text-[12px] xl:text-[13px] font-semibold tracking-[0.16em] xl:tracking-[0.18em] uppercase transition-colors duration-200",
                  isActive
                    ? "text-[color:var(--sp-gold)]"
                    : "text-white/85 hover:text-[color:var(--sp-gold)]"
                )
              }
              end={link.to === "/"}
            >
              {({ isActive }) => (
                <span className="relative whitespace-nowrap">
                  {link.label}
                  {isActive && (
                    <span className="absolute -bottom-2 left-1/2 h-[2px] w-6 -translate-x-1/2 bg-[color:var(--sp-gold)]" />
                  )}
                </span>
              )}
            </NavLink>
          ))}
        </nav>

        {/* Right-side cluster: phone + CTA + (mobile hamburger).
            Both buttons use the animated luxe-gold styles so users see the
            shimmer and pulsing halo on every breakpoint. */}
        <div className="flex items-center gap-2 sm:gap-3 shrink-0">
          {/* Phone — luxe outline pill. Icon only < xl, full text on xl+ */}
          <a
            href={`tel:${contact.whatsappDigits || "919637241414"}`}
            data-testid="navbar-phone-link"
            aria-label={`Call ${contact.whatsapp || "+91 96372 41414"}`}
            className="btn-luxe-gold-outline inline-flex items-center justify-center gap-2 h-10 px-3 sm:px-4 xl:px-5 text-[11px] sm:text-[12px] uppercase whitespace-nowrap"
          >
            <Phone className="luxe-icon luxe-icon-phone h-4 w-4 shrink-0" />
            <span className="hidden xl:inline">{contact.whatsapp || "+91 96372 41414"}</span>
          </a>
          {/* Book a Visit — luxe filled pill with sheen + pulse */}
          <Link
            to="/contact"
            data-testid="navbar-primary-cta-button"
            className="btn-luxe-gold inline-flex items-center gap-2 h-10 px-4 sm:px-5 text-[11px] sm:text-[12px] uppercase whitespace-nowrap"
          >
            <CalendarCheck2 className="luxe-icon luxe-icon-visit h-4 w-4 shrink-0" />
            <span className="sm:hidden">Visit</span>
            <span className="hidden sm:inline">Book a Visit</span>
          </Link>

          {/* Mobile hamburger — kept for full nav, only on < lg */}
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <button
                data-testid="navbar-mobile-menu-button"
                aria-label="Open navigation menu"
                className="lg:hidden inline-flex h-10 w-10 items-center justify-center rounded-md border border-white/10 bg-white/5 backdrop-blur-md text-white hover:border-[color:rgba(212,175,55,0.4)] transition-colors"
              >
                <Menu className="h-5 w-5" />
              </button>
            </SheetTrigger>
            <SheetContent
              side="right"
              className="w-[88%] sm:w-[420px] bg-[#0a0a0a]/95 backdrop-blur-2xl border-l border-white/10 text-white p-0"
            >
              <div className="flex h-full flex-col">
                <div className="flex items-center justify-between border-b border-white/10 px-6 py-5">
                  <Logo variant="full" />
                </div>
                <nav className="flex flex-col gap-1 px-4 py-6">
                  {NAV_LINKS.map((link) => (
                    <NavLink
                      key={link.to}
                      to={link.to}
                      data-testid={`mobile-nav-link-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
                      className={({ isActive }) =>
                        cn(
                          "flex items-center justify-between px-4 py-4 rounded-md border border-transparent transition-colors",
                          "font-display text-xl",
                          isActive
                            ? "border-[color:rgba(212,175,55,0.4)] bg-[color:var(--sp-gold-soft)] text-[color:var(--sp-gold)]"
                            : "text-white/85 hover:text-[color:var(--sp-gold)] hover:bg-white/[0.03]"
                        )
                      }
                      end={link.to === "/"}
                    >
                      {link.label}
                      <span className="text-xs tracking-[0.25em] uppercase text-white/35">
                        →
                      </span>
                    </NavLink>
                  ))}
                </nav>
                {/* CTA cluster inside the drawer — same luxe styling so they
                    feel premium even inside the hamburger sheet. Centred. */}
                <div className="mt-auto border-t border-white/10 p-6 flex flex-col items-stretch gap-3">
                  <a
                    href={`tel:${contact.whatsappDigits || "919637241414"}`}
                    data-testid="mobile-cta-phone"
                    className="btn-luxe-gold-outline inline-flex items-center justify-center gap-3 px-5 py-3 text-[12px] tracking-[0.2em] uppercase"
                  >
                    <Phone className="luxe-icon luxe-icon-phone h-4 w-4" />
                    {contact.whatsapp || "+91 96372 41414"}
                  </a>
                  <Link
                    to="/contact"
                    data-testid="mobile-cta-book-visit"
                    className="btn-luxe-gold inline-flex items-center justify-center gap-3 px-5 py-3 text-[12px] tracking-[0.2em] uppercase"
                  >
                    <CalendarCheck2 className="luxe-icon luxe-icon-visit h-4 w-4" />
                    Book a Site Visit
                  </Link>
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
};

export default Navbar;
